<script setup lang="ts">
/** BON01 — Authority to Issue Shares & Capitalisation of Reserves (DCR + Members' Written Resolution). 2부 문서. */
import { computed, inject } from 'vue';
import { BONUS_DOC_CTX } from './bonusContext';
import BonusDocVarField from './BonusDocVarField.vue';
import BonusDocHead from './parts/BonusDocHead.vue';
import BonusDcrIntro from './parts/BonusDcrIntro.vue';
import BonusSigners from './parts/BonusSigners.vue';

const ctx = inject(BONUS_DOC_CTX)!;
const multiDirector = computed(() => ctx.flag('is_multiple_director_company'));
const multiMember = computed(() => ctx.flag('is_multiple_member'));
const memberAgree = computed(() => (multiMember.value
  ? 'We, being the eligible members of the Company (as defined in Section 298 of the Companies Act, 2016) in respect of this written resolution, agree that the Resolution be so passed.'
  : 'I, being an eligible member of the Company (as defined in Section 298 of the Companies Act, 2016) in respect of this written resolution, agree that the Resolution be so passed.'));
</script>

<template>
  <div class="allot-doc">
    <!-- Part 1 — Directors' Circular Resolution -->
    <BonusDocHead />
    <BonusDcrIntro />

    <p class="doc-sec-title">WRITTEN RESOLUTION</p>
    <p class="doc-p">
      That a written resolution pursuant to Section 290 of the Companies Act, 2016 be circulated to all members for the purpose of
      obtaining the members' approval in respect of the following resolutions :-
    </p>

    <p class="doc-sec-title" style="margin-bottom:2px">ORDINARY RESOLUTION</p>
    <p class="doc-p" style="font-weight:700">(1) AUTHORITY TO ISSUE SHARES</p>
    <p class="doc-p">
      THAT pursuant to Section 75 of the Companies Act, 2016, the Director(s) be and is/are hereby empowered to issue shares in the Company
      from time to time and upon such terms and conditions and for such purposes as the Director(s) may deem fit and that such authority shall
      continue in force until not more than twelve months after the approval is given by the members. Such new shares when fully paid will rank
      pari passu in all respects with existing shares in the Company.
    </p>
    <p class="doc-p" style="font-weight:700">(2) CAPITALISATION OF RESERVES</p>
    <p class="doc-p">
      THAT the sum of RM<BonusDocVarField field-key="reserve_amount" /> be capitalised from the Company's
      <BonusDocVarField field-key="reserve_type" /> and the Director(s) be and is/are hereby authorised to apply the said sum
      in paying in full for new ordinary shares of RM<BonusDocVarField field-key="share_price" /> each in the share capital
      of the Company, such new ordinary shares to be distributed and credited as fully paid-up to those person(s) registered as shareholder(s)
      on <BonusDocVarField field-key="record_date" /> on the basis of
      <BonusDocVarField field-key="bonus_ratio_new" /> (<BonusDocVarField field-key="bonus_ratio_new_words" />)
      new fully paid-up ordinary share(s) for every <BonusDocVarField field-key="bonus_ratio_existing" />
      (<BonusDocVarField field-key="bonus_ratio_existing_words" />) existing ordinary shares held, fractions of an
      ordinary share be ignored and such new ordinary share(s) shall rank pari passu in all respects with the existing ordinary shares.
    </p>

    <p class="doc-lbl-title">BOARD OF DIRECTORS</p>
    <BonusSigners var-key="authority_dcr" board-label="BOARD OF DIRECTORS" />
    <p class="doc-p" style="margin-top:14px"><b>Date:</b> <BonusDocVarField field-key="dcr_date" /></p>

    <!-- Part 2 — Members' Written Resolution -->
    <div class="page-div">— — — Members' Written Resolution — — —</div>
    <BonusDocHead />
    <p class="doc-p">
      Pursuant to Section 290 of the Companies Act, 2016
      <span
        class="doc-toggle"
        title="sole / multiple director (auto from entity)"
        @click="ctx.toggleFlag('is_multiple_director_company')"
      >{{ multiDirector ? 'the directors of the Company propose' : 'the director of the Company proposes' }}</span>
      that the following resolution is passed as an ordinary resolution (the "Resolution")
    </p>
    <p class="doc-sec-title" style="margin-bottom:2px">ORDINARY RESOLUTION</p>
    <p class="doc-p" style="font-weight:700">(1) AUTHORITY TO ISSUE SHARES &nbsp;·&nbsp; (2) CAPITALISATION OF RESERVES</p>
    <p class="doc-p">
      THAT the sum of RM<BonusDocVarField field-key="reserve_amount" /> be capitalised from the Company's
      <BonusDocVarField field-key="reserve_type" />, applied in paying up in full new ordinary shares of
      RM<BonusDocVarField field-key="share_price" /> each, distributed as fully paid-up to shareholders registered on
      <BonusDocVarField field-key="record_date" /> on the basis of
      <BonusDocVarField field-key="bonus_ratio_new" /> new for every
      <BonusDocVarField field-key="bonus_ratio_existing" /> existing ordinary shares held.
    </p>

    <p class="doc-lbl-title">AGREEMENT:</p>
    <p class="doc-p">
      <span class="doc-toggle" title="single / multiple member (auto from entity)" @click="ctx.toggleFlag('is_multiple_member')">{{ memberAgree }}</span>
    </p>
    <p class="doc-p"><b>Signed By: -</b></p>
    <BonusSigners var-key="authority_members" />

    <p class="doc-p" style="margin-top:14px"><b>Circulation date:</b> &nbsp; <BonusDocVarField field-key="circulation_date" /></p>

    <p class="doc-attn">
      <b>Note:</b> To signify your agreement to the Resolution, you must sign this document where indicated above; return the signed document by
      hand or send it by post to <BonusDocVarField field-key="cosec_name" />,
      <BonusDocVarField field-key="cosec_address" />; ensure that the signed document is received by the Company no
      later than <BonusDocVarField field-key="deadline_date" />. If the Resolution is not passed by this date, it will lapse.
      Once given, your agreement may not be revoked.
    </p>
  </div>
</template>
